import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import Loader from '../components/Loader';
import login from "../assets/images/login.png";
import login2 from "../assets/images/login2.png";
import login3 from "../assets/images/login3.png";
import CustomCarousel from '../components/common/Carousel';
import { isInstalledApp } from '../utils/pwa';

// Verify Email Illustration
import verifyEmailIllustration from "../assets/images/verify-email-illustration.png";

// Forgot Password Illustration
import forgotPasswordIllustration from "../assets/images/forgot-password-illustration.png";

const styolaLogo = "/assets/images/logo.png";

const BrandLogo = ({
  width = 168,
  className = "",
}: {
  width?: number;
  className?: string;
}) => (
  <img
    src={styolaLogo}
    alt="Styola"
    className={className}
    style={{
      display: "block",
      width,
      height: "auto",
      maxWidth: "100%",
    }}
  />
);

const AuthLayout: React.FC = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const installedApp = isInstalledApp();

  const isVerifyEmailPage = location.pathname === '/verifyEmail';
  const isForgotPasswordPage = location.pathname === '/forgot-password';

  const carouselItems = [
    {
      img: login,
      quote: "You can never be overdressed or overeducated.",
      author: "Oscar Wilde",
    },
    {
      img: login2,
      quote: "Style is a way to say who you are without having to speak.",
      author: "Rachel Zoe",
    },
    {
      img: login3,
      quote: "Fashion is what you buy. Style is what you do with it.",
      author: "Unknown",
    },
  ];

  useEffect(() => {
    if (!loading && isAuthenticated &&
      (location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/')) {
      navigate('/app/home', { replace: true });
    }
  }, [isAuthenticated, loading, navigate, location.pathname]);

  if (loading || (isAuthenticated &&
    (location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/'))) {
    return <Loader />;
  }

  // Desktop CarouselItem - for left panel with Styola branding
  const DesktopCarouselItem = ({ item }: any) => {
    return (
      <Box className="flex flex-col justify-center items-center relative h-full max-h-screen">
        <img src={item.img} alt="Fashion Quote" className="h-full w-full object-cover" />
        <Box className="absolute inset-0 bg-black/20" />

        <Box className="absolute top-8 left-8 z-10">
          <BrandLogo width={152} />
        </Box>

        <Box className="absolute w-5/6 bottom-[100px] z-10">
          <p className="text-2xl text-white mb-3 text-center font-sk-modernist">
            "{item.quote}"
          </p>
          <p className="text-lg text-white text-right font-sk-modernist">
            -{item.author}
          </p>
        </Box>
      </Box>
    );
  };

  // Verify Email Illustration Panel
  const VerifyEmailIllustration = () => (
    <div className="w-full h-full bg-gray-100 flex flex-col items-start justify-start p-8 md:p-12 relative overflow-hidden">
      <div className="mb-8 z-10">
        <BrandLogo width={176} />
      </div>

      <div className="flex-1 w-full flex items-center justify-center relative">
        <img src={verifyEmailIllustration} alt="Verify Email" className="w-full max-w-[500px] object-contain" />
      </div>
    </div>
  );

  // Mobile Layout for Verify Email
  if (isMobile && isVerifyEmailPage) {
    return (
      <div className="min-h-screen w-full bg-white flex flex-col">
        <div className="flex items-center justify-between px-6 py-3 bg-white shadow-sm">
          {installedApp ? (
            <div className="w-6 h-6"></div>
          ) : (
            <button className="w-6 h-6 flex flex-col justify-center items-center gap-1">
              <span className="w-full h-0.5 bg-black"></span>
              <span className="w-full h-0.5 bg-black"></span>
              <span className="w-full h-0.5 bg-black"></span>
            </button>
          )}

          <div className="flex items-center">
            <BrandLogo width={118} />
          </div>

          <div className="w-6 h-6"></div>
        </div>

        <div className="w-full bg-gray-100 flex items-center justify-center py-6 px-4">
          <img src={verifyEmailIllustration} alt="Verify Email" className="w-full max-w-[280px] object-contain" />
        </div>

        <div className="flex-1 flex justify-center items-start px-4 py-6">
          <Outlet />
        </div>
      </div>
    );
  }

  // Forgot Password Illustration Panel
  const ForgotPasswordIllustration = () => (
    <div className="w-full h-full bg-gray-100 flex flex-col items-start justify-start p-8 md:p-12 relative overflow-hidden">
      <div className="mb-8 z-10">
        <BrandLogo width={176} />
      </div>

      <div className="flex-1 w-full flex items-center justify-center relative">
        <img src={forgotPasswordIllustration} alt="Forgot Password" className="w-full max-w-[500px] object-contain" />
      </div>
    </div>
  );

  // Mobile Layout for Forgot Password
  if (isMobile && isForgotPasswordPage) {
    return (
      <div className="min-h-screen w-full bg-white flex flex-col">
        <div className="flex items-center justify-between px-6 py-3 bg-white shadow-sm">
          {installedApp ? (
            <div className="w-6 h-6"></div>
          ) : (
            <button className="w-6 h-6 flex flex-col justify-center items-center gap-1">
              <span className="w-full h-0.5 bg-black"></span>
              <span className="w-full h-0.5 bg-black"></span>
              <span className="w-full h-0.5 bg-black"></span>
            </button>
          )}

          <div className="flex items-center">
            <BrandLogo width={118} />
          </div>

          <div className="w-6 h-6"></div>
        </div>

        <div className="w-full bg-gray-100 flex items-center justify-center py-6 px-4">
          <img src={forgotPasswordIllustration} alt="Forgot Password" className="w-full max-w-[280px] object-contain" />
        </div>

        <div className="flex-1 flex justify-center items-start px-4 py-6">
          <Outlet />
        </div>
      </div>
    );
  }

  // Standard Mobile Layout
  if (isMobile) {
    const currentItem = carouselItems[currentSlide];

    return (
      <div className="relative min-h-screen w-full bg-gray-900">
        <div className="absolute inset-0">
          <img src={currentItem.img} alt="Background" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative z-50 flex items-center justify-between px-6 py-3 bg-white/95 backdrop-blur-sm shadow-sm">
          {installedApp ? (
            <div className="w-6 h-6"></div>
          ) : (
            <button className="w-6 h-6 flex flex-col justify-center items-center gap-1">
              <span className="w-full h-0.5 bg-black"></span>
              <span className="w-full h-0.5 bg-black"></span>
              <span className="w-full h-0.5 bg-black"></span>
            </button>
          )}

          <div className="flex items-center">
            <BrandLogo width={118} />
          </div>

          <div className="w-6 h-6"></div>
        </div>

        <div className="relative z-10 flex flex-col min-h-[calc(100vh-58px)] pt-4 pb-6 px-4">
          <div className="flex-1 flex items-center justify-center">
            <Outlet />
          </div>

          <div className="mt-auto pt-6">
            <p className="text-sm text-white text-center font-sk-modernist mb-2">
              "{currentItem.quote}"
            </p>
            <p className="text-xs text-white text-right font-sk-modernist">
              -{currentItem.author}
            </p>

            <div className="flex justify-center gap-2 mt-4">
              {carouselItems.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-1 rounded-full transition-all ${index === currentSlide ? 'w-8 bg-styola-primary' : 'w-1 bg-white/50'}`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop Layout for Verify Email
  if (isVerifyEmailPage) {
    return (
      <div className="flex items-center justify-center bg-gray-100">
        <div className="flex flex-row min-h-screen w-full bg-white relative">
          <div className="w-1/2">
            <VerifyEmailIllustration />
          </div>
          <Outlet />
        </div>
      </div>
    );
  }

  // Desktop Layout for Forgot Password
  if (isForgotPasswordPage) {
    return (
      <div className="flex items-center justify-center bg-gray-100">
        <div className="flex flex-row min-h-screen w-full bg-white relative">
          <div className="w-1/2">
            <ForgotPasswordIllustration />
          </div>
          <Outlet />
        </div>
      </div>
    );
  }

  // Standard Desktop Layout
  return (
    <div className="flex items-center justify-center bg-gray-100">
      <div className="flex flex-row min-h-screen w-full bg-white relative">
        <div className="w-1/2 flex justify-center items-center bg-orange-100">
          <CustomCarousel autoPlay={true} interval={10000} infiniteLoop={true}>
            {carouselItems.map((item, i) => (
              <DesktopCarouselItem key={i} item={item} />
            ))}
          </CustomCarousel>
        </div>

        <Outlet />
      </div>
    </div>
  );
};

export default AuthLayout;
